import { type TableProps, Typography } from 'antd';
import type { CheckboxProps } from 'antd/es/checkbox/Checkbox';
import type { ColumnsType } from 'antd/es/table';
import type { TFunction } from 'i18next';
import moment from 'moment';
import { type AriaAttributes, type ComponentType, type Key, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { StyledTable as Table } from 'components/DataTable/DataTable.style';
import { Tag } from 'components/ui';
import type { SkydioMediaFile } from 'utils/skydio';

import { mediaKindLabels, shortId } from './helpers';
import { SkydioMediaPreview } from './SkydioMediaPreview';

type SkydioMediaTableProps = {
  disabled?: boolean;
  loading: boolean;
  media: SkydioMediaFile[];
  selectedRowKeys: Key[];
  onSelectionChange: (keys: Key[]) => void;
};

const TypedTable = Table as ComponentType<TableProps<SkydioMediaFile>>;

const SkydioTable = styled(TypedTable)`
  width: 100%;

  .ant-table {
    table-layout: fixed;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    padding-left: 12px;
    padding-right: 12px;
  }

  .ant-table-tbody > tr > td,
  .ant-table-tbody .ant-typography {
    color: ${({ theme }) => theme.textColor};
  }

  .ant-table-thead > tr > th {
    color: ${({ theme }) => theme.labelColor} !important;
  }

  .ant-table-cell {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ant-tag {
    color: ${({ theme }) => theme.headerColor};
    line-height: 18px;
  }
`;

type AccessibleCheckboxProps = Partial<Omit<CheckboxProps, 'checked' | 'defaultChecked'>> &
  AriaAttributes;

const accessibleCheckboxProps = (label: string): AccessibleCheckboxProps => ({
  'aria-label': label,
});

const getColumns = (ingestedColor: string, t: TFunction): ColumnsType<SkydioMediaFile> => [
  {
    title: t('inspection.skydio.previewColumn', 'Preview'),
    key: 'preview',
    width: 72,
    render: (_value: unknown, item: SkydioMediaFile) => {
      const previewUrl = item.thumbnailUrl ?? item.previewUrl ?? undefined;
      const fullSizeUrl = item.previewUrl ?? previewUrl;
      return (
        <SkydioMediaPreview
          key={previewUrl}
          filename={item.filename}
          previewUrl={previewUrl}
          fullSizeUrl={fullSizeUrl}
        />
      );
    },
  },
  {
    title: t('inspection.skydio.filenameColumn', 'Filename'),
    dataIndex: 'filename',
    key: 'filename',
    width: 155,
    ellipsis: true,
    render: (value: string) => (
      <Typography.Text title={value} ellipsis>
        {value}
      </Typography.Text>
    ),
  },
  {
    title: t('inspection.skydio.kindColumn', 'Kind'),
    dataIndex: 'kind',
    key: 'kind',
    width: 85,
    render: (value: string) => (
      <Tag>
        {value === 'vehicleRadiometricImage'
          ? t('inspection.skydio.thermal', 'Thermal')
          : mediaKindLabels[value] ?? value}
      </Tag>
    ),
  },
  {
    title: t('inspection.skydio.capturedColumn', 'Captured'),
    dataIndex: 'capturedTime',
    key: 'capturedTime',
    width: 145,
    render: (value: string) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : ''),
  },
  {
    title: t('inspection.skydio.flightColumn', 'Flight'),
    dataIndex: 'flightId',
    key: 'flightId',
    width: 125,
    ellipsis: true,
    render: (value: string) => (
      <Typography.Text title={value} ellipsis>
        {shortId(value)}
      </Typography.Text>
    ),
  },
  {
    title: t('inspection.skydio.statusColumn', 'Status'),
    dataIndex: 'ingested',
    key: 'ingested',
    width: 75,
    render: (value: boolean, record: SkydioMediaFile) => (
      <Tag color={value ? ingestedColor : undefined}>
        {record.ingestionStatusKnown === false
          ? t('inspection.skydio.unknownStatus', 'Unknown')
          : value
            ? t('inspection.skydio.ingestedStatus', 'Ingested')
            : t('inspection.skydio.newStatus', 'New')}
      </Tag>
    ),
  },
];

export function SkydioMediaTable({
  disabled = false,
  loading,
  media,
  selectedRowKeys,
  onSelectionChange,
}: SkydioMediaTableProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const columns = useMemo(() => getColumns(theme.green, t), [t, theme.green]);

  return (
    <SkydioTable
      rowKey="uuid"
      loading={loading}
      size="small"
      scroll={{ x: 705, y: 320 }}
      pagination={{ pageSize: 10, size: 'small', showSizeChanger: false }}
      rowSelection={{
        columnWidth: 40,
        selectedRowKeys,
        onChange: onSelectionChange,
        getCheckboxProps: record => ({
          ...accessibleCheckboxProps(
            t('inspection.skydio.selectMedia', 'Select {{filename}}', {
              filename: record.filename,
            })
          ),
          disabled,
        }),
      }}
      columns={columns}
      dataSource={media}
    />
  );
}
